import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, DropdownProps, Icon } from 'semantic-ui-react';
import { CompositeDecorator, Editor, EditorState, RichUtils, getDefaultKeyBinding, convertFromHTML, ContentState, AtomicBlockUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import 'draft-js/dist/Draft.css';
import styles from './RichTextEditor.module.css';
import axiosInstance from '../../../helpers/axiosInstance';

interface RichTextEditorProps {
  onFocus?: any;
  isFocus?: any;
  onBlur?: any;
  onChange: (editorState: EditorState, isBlur?: boolean) => void;
  editorState: EditorState;
  rows?: number;
  theme?: 'light' | 'dark';
  showHtml?: any;
  ccEmails?: any;
  setCcEmails?: any;
  selectedCcEmails?: any;
  setSelectedCcEmails?: any;
  isHtmlOrPlainText?: boolean;
  showImageUpload?: boolean;
}

const DraftLink = (props: any) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a href={url} style={{ color: '#3b5998', textDecoration: 'underline' }}>
      {props.children}
    </a>
  );
};

function findLinkEntities(contentBlock: any, callback: any, contentState: any) {
  contentBlock.findEntityRanges(
    (character: any) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'LINK'
      );
    },
    callback
  );
}

const InlineImage = (props: any) => {
  const data = props.contentState.getEntity(props.entityKey).getData();
  const src = data.src;
  const alt = data.alt;

  return <img src={src} alt={alt} />;
};

function findInlineImageEntities(contentBlock: any, callback: any, contentState: any) {
  contentBlock.findEntityRanges(
    (character: any) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'IMAGE'
      );
    },
    callback
  );
}

export const decoratorPlugin = () => new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: DraftLink
  },
  {
    strategy: findInlineImageEntities,
    component: InlineImage
  }
]);

const RichTextEditor: React.FC<RichTextEditorProps> = React.memo(({ onFocus, isFocus = true, onBlur, onChange, editorState, rows = 24, theme = 'dark', showHtml, ccEmails, setCcEmails, selectedCcEmails, setSelectedCcEmails, isHtmlOrPlainText = true, showImageUpload = false }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [isShowCcEmails, setIsShowCcEmails] = useState<boolean>(false);
  const [ccEmailOptions, setCcEmailOptions] = useState<any>([]);

  useEffect(() => {
    if (ccEmails && ccEmails.length > 0) {
      overwriteCcEmails(ccEmails);
    }
  }, [ccEmails]);

  function overwriteCcEmails(ccEmails: any) {
    const tmpCcEmailOptions: any = [];
    ccEmails.map((ccEmail: string) => {
      tmpCcEmailOptions.push({
        text: ccEmail,
        value: ccEmail,
      });
    });
    setCcEmailOptions(tmpCcEmailOptions);
  }

  function moveFocusToStart(editorState: EditorState): EditorState {
    const selectionState = editorState.getSelection();
    return EditorState.forceSelection(
      editorState,
      selectionState.merge({
        anchorOffset: 0,
        focusOffset: 0,
      }),
    );
  }

  useEffect(() => {
    if (isFocus) {
      onChange(moveFocusToStart(editorState));
    }
  }, [isFocus]);

  const handleKeyCommand = (command: string, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);

    if (newState) {
      onChange(newState);
      return 'handled';
    }

    return 'not-handled';
  };

  const toggleStyle = (style: string) => {
    const newEditorState = RichUtils.toggleInlineStyle(editorState, style);
    onChange(newEditorState);
  };

  const toggleBlockType = (blockType: string) => {
    const newEditorState = RichUtils.toggleBlockType(editorState, blockType);
    onChange(newEditorState);
  };

  const addHttpPrefix = (url: string) => {
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "http://" + url;
    }
    return url;
  }

  const toggleLink = () => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      let href = window.prompt('Enter a link');
      if (href) {
        href = addHttpPrefix(href);
        const contentStateWithEntity = contentState.createEntity(
          'LINK',
          'MUTABLE',
          { url: href }
        );
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
        const updatedEditorState = RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey);
        const blocksFromHTML = convertFromHTML(stateToHTML(updatedEditorState.getCurrentContent()));
        const tmpContentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        onChange(EditorState.createWithContent(tmpContentState, decoratorPlugin()));
      }
    }
  };

  const removeLink = () => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      onChange(RichUtils.toggleLink(editorState, selection, null));
    }
  }

  const rowHeight = 24; // You can adjust this value to match the line height in your editor
  const editorHeight = rows * rowHeight;
  const darkTheme = theme === 'dark';

  const selection = editorState.getSelection();

  const keyBindingFn = (e: any) => {
    if (e.keyCode === 13 && e.shiftKey) {
      return 'soft-line-break';
    }
    return getDefaultKeyBinding(e);
  }

  const handleReturn = (e: any, editorState: any) => {
    if (e.shiftKey) {
      onChange(RichUtils.insertSoftNewline(editorState));
      return 'handled';
    }
    return 'not-handled';
  }

  const onHandleAddition = (e: React.SyntheticEvent<HTMLElement, Event>, dropdownProps: DropdownProps) => {
    const { value } = dropdownProps;
    if (typeof value === 'string') {
      setSearchValue('');
      setCcEmails([...ccEmails, value]);
      setSelectedCcEmails([...selectedCcEmails, value]);
      setCcEmailOptions([{ text: value, value }, ...ccEmailOptions]);
    }
  }

  const onHandleMuliSelect = (event: React.SyntheticEvent<HTMLElement>, dropdownProps: DropdownProps) => {
    const { value }: any = dropdownProps;
    setSelectedCcEmails(value);
    setSearchValue('');
  }

  const handleEditorChange = (newEditorState: any) => {
    // Accessing event.target when the editor content changes
    // For example, you can check if the target is outside the editor container
    if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
      onChange(newEditorState, true);
    } else {
      onChange(newEditorState);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === ' ') {
      e.preventDefault();
    }
  };

  const onHandleSearchChange = async (e: any, { searchQuery }: any) => {
    setSearchValue(searchQuery.trim());
    if (searchQuery.trim()) {
      await axiosInstance.get(`/Case/AddressbookLookup/${searchQuery.trim()}`).then(({ data, status }) => {
        if (status === 200) {
          setCcEmailOptions((prevState: any) => {
            const tmpCcEmailOptions = [...prevState];
            data.forEach((email: string) => {
              const isEmailPresent = tmpCcEmailOptions.some(option => option.value === email);
              if (!isEmailPresent) {
                tmpCcEmailOptions.push({
                  text: email,
                  value: email,
                });
              }
            });
            return tmpCcEmailOptions;
          });
        }
      })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  const validateImageDimensions = (file: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width === 540 && img.height === 200) {
          resolve();
        } else {
          reject("Image dimensions must be 540x200 pixels.");
        }
      };
      img.onerror = () => reject("Invalid image file.");
      img.src = URL.createObjectURL(file);
    });
  };

  const insertImage = (imageUrl: any) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "IMAGE",
      "IMMUTABLE",
      { src: imageUrl }
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      editorState,
      entityKey,
      " "
    );
    onChange(newEditorState);
  };

  const handleFileSelect = (file: any) => {
    const reader = new FileReader();
    reader.onload = () => {
      insertImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = async (event: any) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await validateImageDimensions(file); // Validate dimensions
        handleFileSelect(file);
      } catch (validationError) {
        alert(validationError);
      }
    }
  };

  const handlePastedFiles = (files: any) => {
    const file = files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        insertImage(reader.result);
      };
      reader.readAsDataURL(file);
      return 'handled';
    }
    return 'not-handled';
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text").trim();
    setSearchValue(pastedText);
  };

  return (
    <div>
      <div className={`${styles.toolbar} ${darkTheme ? styles.dark : ''}`} style={{ display: 'grid' }}>
        {ccEmails && (ccEmails.length > 0 || isShowCcEmails) && (
          <div className='cc-email' style={{ display: 'flex', marginBottom: '1rem' }}>
            <span style={{ marginTop: '1rem', marginRight: '1rem' }}>Cc:</span>
            <Dropdown
              fluid
              multiple
              search
              selection
              clearable
              allowAdditions
              additionLabel='Click here to add '
              noResultsMessage={null}
              options={ccEmailOptions}
              onChange={onHandleMuliSelect}
              onAddItem={onHandleAddition}
              defaultValue={selectedCcEmails}
              onSearchChange={onHandleSearchChange}
              onKeyDown={onKeyDown}
              searchQuery={searchValue}
              searchInput={{ onPaste: handlePaste }}
            />
            {ccEmails && ccEmails.length === 0 && (
              <span><Icon name='remove' style={{ cursor: 'pointer', marginTop: '1rem', marginLeft: '1rem' }} onClick={() => setIsShowCcEmails(false)} /></span>
            )}
          </div>
        )}
        <div>
          <button onClick={() => toggleStyle('BOLD')}><Icon name="bold" /></button>
          <button onClick={() => toggleStyle('ITALIC')}><Icon name="italic" /></button>
          <button onClick={() => toggleStyle('UNDERLINE')}><Icon name="underline" /></button>
          <button onClick={() => toggleBlockType('ordered-list-item')}><Icon name="list ol" /></button>
          <button onClick={() => toggleBlockType('unordered-list-item')}><Icon name="list ul" /></button>
          {onFocus && (
            <>
              <button onClick={() => toggleLink()}><Icon name="linkify" style={{ color: selection.isCollapsed() ? '#bababa' : '#000' }} /></button>
              <button onClick={() => removeLink()}><Icon name="unlink" style={{ color: selection.isCollapsed() ? '#bababa' : '#000' }} /></button>
            </>
          )}
          {/* {showImageUpload && (
            <>
              <button onClick={() => fileInputRef.current?.click()}><Icon name="image" /></button>
              <input type="file" accept="image/*" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
            </>
          )} */}
          {ccEmails && ccEmails.length === 0 && !isShowCcEmails && (
            <div style={{ cursor: 'pointer', float: 'right', textDecoration: 'underline' }} onClick={() => setIsShowCcEmails(true)}>Cc</div>
          )}
        </div>
      </div>
      <div ref={containerRef} className={`${styles.editorContainer} ${darkTheme ? styles.dark : ''}`} style={{ height: editorHeight + 'px', resize: 'vertical', overflow: 'auto' }} onClick={(e: any) => {
        if (e.target.className.indexOf('RichTextEditor_editorContainer') !== -1) {
          onChange(EditorState.moveFocusToEnd(editorState));
          if (onFocus) {
            onFocus();
          }
        }
      }}>
        {(onFocus === undefined || isFocus === true) && (
          <Editor
            editorState={editorState}
            handleKeyCommand={handleKeyCommand}
            handlePastedFiles={handlePastedFiles}
            keyBindingFn={keyBindingFn}
            handleReturn={handleReturn}
            onChange={handleEditorChange}
            onFocus={onFocus}
            onBlur={onBlur}
            spellCheck={true}
            ref={editorRef}
          />
        )}
        {showHtml && !isFocus && (
          <div className='editor-content' onClick={(e: any) => {
            onChange(EditorState.moveFocusToEnd(editorState));
            if (onFocus) {
              onFocus();
            }
          }}>
            <div dangerouslySetInnerHTML={{ __html: isHtmlOrPlainText ? (showHtml || '').replace(/href/g, `target='_blank' href`) : showHtml.split('\n').join('<br/>') }} />
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.editorState === nextProps.editorState &&
    prevProps.isFocus === nextProps.isFocus &&
    prevProps.theme === nextProps.theme &&
    prevProps.showHtml === nextProps.showHtml;
});

export default RichTextEditor;
